import { useId } from "react";
import type { LucideIcon } from "lucide-react";

import { Row } from "@/components";
import { cn } from "@/lib/utils";

import type { HexGradient } from "@/types";

const HEX_POINTS = "20,1 37,11 37,33 20,43 3,33 3,11";

// "card" is the large hex on the top-performer cards; "table" is the small hex
// shown next to leading staffers' names in the leaderboard table.
type HexBadgeSize = "card" | "table";

const SIZE_STYLES: Record<
  HexBadgeSize,
  {
    box: string;
    icon: string;
    backdrop: string;
    fill: { top: string; mid: string; bottom: string };
    shine: { start: string; mid: string };
  }
> = {
  card: {
    box: "h-[55px] w-[50px]",
    icon: "h-5 w-5",
    backdrop: "inset-[3px]",
    fill: { top: "0.52", mid: "0.38", bottom: "0.48" },
    shine: { start: "0.4", mid: "0.08" },
  },
  table: {
    box: "h-8 w-7",
    icon: "h-3 w-3",
    backdrop: "inset-[2px]",
    fill: { top: "0.48", mid: "0.32", bottom: "0.42" },
    shine: { start: "0.35", mid: "0.06" },
  },
};

export const HexagonIconBadge = ({
  icon: Icon,
  gradient,
  size = "card",
  backdropColor,
}: {
  icon: LucideIcon;
  gradient: HexGradient;
  size?: HexBadgeSize;
  backdropColor?: string;
}) => {
  const uid = useId().replace(/:/g, "");
  const fillId = `hex-fill-${uid}`;
  const shineId = `hex-shine-${uid}`;
  const styles = SIZE_STYLES[size];

  return (
    <Row className={cn("relative shrink-0", styles.box)}>
      {backdropColor ? (
        <Row
          className={cn(
            "absolute [clip-path:polygon(50%_0%,92.5%_25%,92.5%_75%,50%_100%,7.5%_75%,7.5%_25%)]",
            styles.backdrop,
          )}
          style={{ backgroundColor: backdropColor }}
        />
      ) : null}
      <svg viewBox="0 0 40 44" className="h-full w-full">
        <defs>
          <linearGradient
            id={fillId}
            x1="20"
            y1="2"
            x2="20"
            y2="42"
            gradientUnits="userSpaceOnUse"
          >
            <stop
              offset="0%"
              stopColor={gradient.top}
              stopOpacity={styles.fill.top}
            />
            <stop
              offset="45%"
              stopColor={gradient.mid}
              stopOpacity={styles.fill.mid}
            />
            <stop
              offset="100%"
              stopColor={gradient.bottom}
              stopOpacity={styles.fill.bottom}
            />
          </linearGradient>
          <linearGradient
            id={shineId}
            x1="10"
            y1="4"
            x2="30"
            y2="26"
            gradientUnits="userSpaceOnUse"
          >
            <stop
              offset="0%"
              stopColor="#ffffff"
              stopOpacity={styles.shine.start}
            />
            <stop
              offset="50%"
              stopColor="#ffffff"
              stopOpacity={styles.shine.mid}
            />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={HEX_POINTS} fill={`url(#${fillId})`} stroke="none" />
        <polygon points={HEX_POINTS} fill={`url(#${shineId})`} stroke="none" />
      </svg>
      <Row className="absolute inset-0 items-center justify-center">
        <Icon className={cn("text-slate-900", styles.icon)} />
      </Row>
    </Row>
  );
};
